import React, { useEffect, useMemo, useRef, useState } from "react";
import { FormControl, MenuItem, Select, Table, TableBody, TableCell, TableHead, TableRow, TableSortLabel, useTheme } from "@mui/material";
import { XMarkIcon } from "@heroicons/react/20/solid";
import { CiSearch } from "react-icons/ci";
import {
  ConfirmDialog,
  ErrorBox,
  Notice,
  OrgModal,
  Pager,
  PrimaryButton,
  Spinner,
  StatusBadge,
  TextButton,
} from "./components/organisation/OrgModal";
import OrgDetailsModal from "./components/subscription/OrgDetailsModal";
import { PlanBadge } from "./components/subscription/subscriptionUi";
import usePublicPlans from "./hooks/usePublicPlans";
import useResetOnReturn from "./hooks/useResetOnReturn";
import { formatDate, fetchAdminOrgPlan, planDisplayName, planOf, PLAN_STATE, planState, trialDaysLeft, useAdminOrgPlan } from "./utils/subscriptionData";
import { validateEmail, validateName } from "./components/auth/validationHelpers";
import {
  createOrganisation,
  fetchAllOrganisations,
  ORG_STATUS,
  orgErrorMessage,
  startSession,
  switchOrganisation,
  updateOrganisationAsAdmin,
} from "./utils/organisationApi";

const emptyForm = { name: "", ownerFirstName: "", ownerLastName: "", ownerEmail: "", ownerUserId: "" };

// Filtering and sorting by plan. The organisations list carries no plan, so these need every organisation's plan.
const PLAN_FILTER = { NONE: "__none", ENDED: "__ended", TRIAL: "__trial" };

const planKeyOf = (plan) => {
  switch (planState(plan)) {
    case PLAN_STATE.LEGACY:
      return PLAN_FILTER.NONE;
    case PLAN_STATE.NONE:
      return PLAN_FILTER.ENDED;
    case PLAN_STATE.TRIAL:
      return PLAN_FILTER.TRIAL;
    default:
      return planOf(plan)?.plan_id || "";
  }
};
const planSortName = (plan) => (planState(plan) === PLAN_STATE.LEGACY ? "No plan" : planDisplayName(plan)).toLowerCase();
const FETCH_PAGE = 100;
const MAX_FETCH_PAGES = 50;
const fetchEveryOrganisation = async ({ search, status }) => {
  const all = [];
  for (let page = 1; page <= MAX_FETCH_PAGES; page += 1) {
    const result = await fetchAllOrganisations({ search, status, page, limit: FETCH_PAGE });
    all.push(...result.organisations);
    if (!result.organisations.length || all.length >= result.count) break;
  }
  return all;
};

// Runs `task` over `items` with at most `limit` in flight, keeping the order of the results
const mapWithLimit = async (items, limit, task) => {
  const results = new Array(items.length);
  let next = 0;
  const worker = async () => {
    while (next < items.length) {
      const index = next;
      next += 1;
      results[index] = await task(items[index]);
    }
  };
  await Promise.all(Array.from({ length: Math.min(limit, items.length) }, worker));
  return results;
};

// A compact select in the toolbar, styled like the status filter
const FilterSelect = ({ label, value, onChange, minWidth = 160, children }) => {
  const theme = useTheme();
  return (
    <FormControl
      size="small"
      sx={{
        minWidth,
        backgroundColor: theme.palette.primary.main,
        borderRadius: "4px",
        border: `1px solid ${theme.palette.secondary.main}`,
        "& .MuiInputBase-root": {
          height: "30px",
          paddingTop: "4px",
          paddingBottom: "4px",
          paddingLeft: "10px",
          paddingRight: "10px",
          boxSizing: "border-box",
          color: theme.palette.text.main,
          [theme.breakpoints.down("md")]: { fontSize: "14px" },
        },
        "& .MuiSelect-select": { padding: 0, display: "flex", alignItems: "center" },
        "& .MuiSelect-icon": { color: theme.palette.text.main },
        "& fieldset": { border: "none" },
      }}
    >
      <Select value={value} onChange={(e) => onChange(e.target.value)} displayEmpty size="small" inputProps={{ "aria-label": label }}>
        {children}
      </Select>
    </FormControl>
  );
};

// Plan name with a one-line status under it. One request per row, shared with the details modal.
const OrgPlanCell = ({ orgId }) => {
  const { data, loading, error } = useAdminOrgPlan(orgId);
  if (loading || error || !data) return <span className="text-accent-gray-light">{loading ? "Loading..." : "Unavailable"}</span>;
  const { plan } = data;
  const state = planState(plan);
  const row = planOf(plan);
  if (state === PLAN_STATE.LEGACY) return <PlanBadge plan={plan} />;
  const days = trialDaysLeft(plan);
  // The badge says the current status; the line under it adds the plan's name when the badge doesn't
  const name = row?.name && !(state === PLAN_STATE.NONE && row.ended_reason === "trial_expired") ? `${row.name} · ` : "";
  const caption =
    state === PLAN_STATE.TRIAL
      ? `${days} ${days === 1 ? "day" : "days"} left`
      : state === PLAN_STATE.NONE
        ? `${name}Ended ${formatDate(row?.end_at)}`
        : state === PLAN_STATE.ENDING
          ? `${name}Ends ${formatDate(plan.end_at)}`
          : state === PLAN_STATE.PAST_DUE
            ? `${name}Payment due`
            : `Ends ${formatDate(plan.current_period_end)}`;
  return (
    <div className="flex flex-col items-start">
      <PlanBadge plan={plan} />
      <span className="mt-0.5 text-xs text-accent-gray-light">{caption}</span>
    </div>
  );
};

// Create an organisation. Editing one happens in its details modal.
const OrganisationFormModal = ({ isOpen, onClose, onSaved }) => {
  const theme = useTheme();
  const [form, setForm] = useState(emptyForm);
  const [error, setError] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!isOpen) return;
    setForm(emptyForm);
    setError("");
  }, [isOpen]);

  const update = (field) => (e) => setForm((prev) => ({ ...prev, [field]: e.target.value }));

  const handleSubmit = async () => {
    const name = form.name.trim();
    const ownerFirstName = form.ownerFirstName.trim();
    const ownerLastName = form.ownerLastName.trim();
    const ownerEmail = form.ownerEmail.trim();
    if (!name) return setError("Organisation name is required.");
    const firstNameError = validateName(ownerFirstName);
    if (firstNameError) return setError(firstNameError);
    const emailError = validateEmail(ownerEmail);
    if (emailError) return setError(`Owner: ${emailError}`);

    setSaving(true);
    setError("");
    try {
      const created = await createOrganisation({ name, ownerEmail, ownerFirstName, ownerLastName });
      const invite = created.ownerInvited ? ` ${ownerEmail} will get an invite to become Owner.` : "";
      onSaved(`${name} was created.${invite}`);
      onClose();
    } catch (err) {
      setError(orgErrorMessage(err, "Could not create the organisation."));
    } finally {
      setSaving(false);
    }
  };

  const inputClass = "w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:bg-gray-50";
  const inputStyle = { borderColor: theme.palette.secondary.v4, color: theme.palette.text.main };

  return (
    <OrgModal
      isOpen={isOpen}
      onClose={saving ? () => {} : onClose}
      titleId="org-form-title"
      title="Create Organisation"
      footer={
        <>
          <TextButton onClick={onClose} disabled={saving}>
            Cancel
          </TextButton>
          <PrimaryButton onClick={handleSubmit} disabled={saving}>
            {saving ? "Saving..." : "Create Organisation"}
          </PrimaryButton>
        </>
      }
    >
      <p className="text-sm text-accent-gray">
        This organisation has no company domain, so it never matches a sign-up. If the owner has no account yet, they get an invite to become Owner.
      </p>
      <div>
        <label htmlFor="org-form-name" className="block text-sm font-medium mb-2">
          Organisation name <span className="text-red-500">*</span>
        </label>
        <input id="org-form-name" type="text" value={form.name} onChange={update("name")} maxLength={120} className={inputClass} style={inputStyle} autoFocus />
      </div>
      <div className="flex gap-3">
        <div className="flex-1">
          <label htmlFor="org-form-owner-first-name" className="block text-sm font-medium mb-2">
            Owner first name <span className="text-red-500">*</span>
          </label>
          <input id="org-form-owner-first-name" type="text" value={form.ownerFirstName} onChange={update("ownerFirstName")} placeholder="Enter first name" className={inputClass} style={inputStyle} />
        </div>
        <div className="flex-1">
          <label htmlFor="org-form-owner-last-name" className="block text-sm font-medium mb-2">
            Owner last name
          </label>
          <input id="org-form-owner-last-name" type="text" value={form.ownerLastName} onChange={update("ownerLastName")} placeholder="Enter last name" className={inputClass} style={inputStyle} />
        </div>
      </div>
      <div>
        <label htmlFor="org-form-owner" className="block text-sm font-medium mb-2">
          Owner email <span className="text-red-500">*</span>
        </label>
        <input id="org-form-owner" type="email" value={form.ownerEmail} onChange={update("ownerEmail")} placeholder="owner@acme.com" className={inputClass} style={inputStyle} />
      </div>
      <ErrorBox>{error}</ErrorBox>
    </OrgModal>
  );
};

const SuperAdminPage = () => {
  const theme = useTheme();
  const [organisations, setOrganisations] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [isLoading, setIsLoading] = useState(true);
  const [listError, setListError] = useState("");
  const [searchQuery, setSearchQuery] = useState("");
  const [appliedSearch, setAppliedSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [planFilter, setPlanFilter] = useState("");
  const [planSort, setPlanSort] = useState("");
  // Used while a plan filter or sort is on: every organisation and its plan, filtered and paged here
  const [everyOrg, setEveryOrg] = useState([]);
  const [planMap, setPlanMap] = useState({});
  const { plans: catalog } = usePublicPlans();
  const clientMode = Boolean(planFilter) || Boolean(planSort);
  const [currentPage, setCurrentPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [notice, setNotice] = useState("");
  const [formTarget, setFormTarget] = useState(undefined);
  const [planTarget, setPlanTarget] = useState(null);
  const [confirm, setConfirm] = useState(null);
  const [confirmBusy, setConfirmBusy] = useState(false);
  const [confirmError, setConfirmError] = useState("");
  const [typedName, setTypedName] = useState("");
  const [enteringId, setEnteringId] = useState("");
  // Back from an organisation that was just entered: the Enter button must not stay on "Opening..."
  useResetOnReturn(() => setEnteringId(""));
  const skipSearchReset = useRef(true);

  const load = async () => {
    setIsLoading(true);
    setListError("");
    try {
      if (clientMode) {
        const all = await fetchEveryOrganisation({ search: appliedSearch, status: statusFilter });
        // Plans come from the same cache the table rows use, so the rows show up already filled in
        const plans = await mapWithLimit(all, 6, (org) => fetchAdminOrgPlan(org.id).then((body) => body.plan ?? null).catch(() => undefined));
        setEveryOrg(all);
        setPlanMap(Object.fromEntries(all.map((org, index) => [org.id, plans[index]])));
        return all;
      }
      const result = await fetchAllOrganisations({
        search: appliedSearch,
        status: statusFilter,
        page: currentPage,
        limit: rowsPerPage,
      });
      setOrganisations(result.organisations);
      setTotalCount(result.count);
      return result.organisations;
    } catch (err) {
      setOrganisations([]);
      setEveryOrg([]);
      setTotalCount(0);
      setListError(orgErrorMessage(err, "Could not load organisations."));
      return null;
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (skipSearchReset.current) {
      skipSearchReset.current = false;
      return undefined;
    }
    const timer = setTimeout(() => {
      setAppliedSearch(searchQuery.trim());
      setCurrentPage(1);
    }, 300);
    return () => clearTimeout(timer);
  }, [searchQuery]);

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [appliedSearch, statusFilter, clientMode, clientMode ? 0 : currentPage, clientMode ? 0 : rowsPerPage]);

  // Plan filter and sort over everything that was loaded, then the page being looked at
  const planRows = useMemo(() => {
    if (!clientMode) return [];
    const matches = (org) => {
      if (!planFilter) return true;
      const plan = planMap[org.id];
      return plan !== undefined && planKeyOf(plan) === planFilter;
    };
    const filtered = everyOrg.filter(matches);
    if (!planSort) return filtered;
    // Organisations whose plan couldn't be read go last whichever way it is sorted
    const known = (org) => planMap[org.id] !== undefined;
    return [...filtered].sort((a, b) => {
      if (known(a) !== known(b)) return known(a) ? -1 : 1;
      if (!known(a)) return 0;
      const planA = planMap[a.id];
      const planB = planMap[b.id];
      const order = planSortName(planA).localeCompare(planSortName(planB));
      return planSort === "plan-desc" ? -order : order;
    });
  }, [clientMode, everyOrg, planMap, planFilter, planSort]);

  const effectiveTotal = clientMode ? planRows.length : totalCount;
  const totalPages = Math.ceil(effectiveTotal / rowsPerPage) || 1;
  const rows = clientMode ? planRows.slice((currentPage - 1) * rowsPerPage, currentPage * rowsPerPage) : organisations;

  const handleEnter = async (org) => {
    setEnteringId(org.id);
    setListError("");
    try {
      const token = await switchOrganisation(org.id);
      if (!token) throw new Error("No session returned.");
      startSession(token, "/jobs");
    } catch (err) {
      setListError(orgErrorMessage(err, `Could not open ${org.name}.`));
      setEnteringId("");
    }
  };

  const openConfirm = (config) => {
    setConfirmError("");
    setTypedName("");
    setConfirm(config);
  };

  const runConfirm = async () => {
    if (!confirm) return;
    setConfirmBusy(true);
    setConfirmError("");
    try {
      await confirm.action();
      setNotice(confirm.success);
      setConfirm(null);
      // The details modal stays open underneath, so show the new status there straight away
      if (confirm.patch) setPlanTarget((current) => (current ? { ...current, ...confirm.patch } : current));
      load();
    } catch (err) {
      setConfirmError(orgErrorMessage(err, "Something went wrong. Try again."));
    } finally {
      setConfirmBusy(false);
    }
  };

  const toggleSuspend = (org) => {
    const suspending = org.status !== ORG_STATUS.SUSPENDED;
    openConfirm({
      title: suspending ? "Suspend Organisation" : "Reactivate Organisation",
      message: suspending ? (
        <p>
          Suspend <span className="font-bold">{org.name}</span>? Members lose access until it's reactivated. You can still
          open it.
        </p>
      ) : (
        <p>
          Reactivate <span className="font-bold">{org.name}</span>? Members can sign in again straight away.
        </p>
      ),
      confirmLabel: suspending ? "Suspend" : "Reactivate",
      busyLabel: suspending ? "Suspending..." : "Reactivating...",
      danger: suspending,
      success: `${org.name} was ${suspending ? "suspended" : "reactivated"}.`,
      patch: { status: suspending ? ORG_STATUS.SUSPENDED : ORG_STATUS.ACTIVE },
      action: () => updateOrganisationAsAdmin(org.id, { status: suspending ? ORG_STATUS.SUSPENDED : ORG_STATUS.ACTIVE }),
    });
  };

  const headerCellSx = {
    backgroundColor: theme.palette.secondary.v5,
    color: theme.palette.text.main,
    zIndex: 10,
    paddingLeft: 2,
  };
  const cellSx = { paddingLeft: 2, paddingRight: 2 };

  return (
    <div
      className="flex flex-col w-full h-full"
      style={{ backgroundColor: theme.palette.primary.v2, color: theme.palette.text.main }}
    >
      <div className="flex flex-col md:justify-between lg:flex-row items-start lg:items-center gap-4 px-6 max-md:px-4 pt-4">
        <div className="relative w-full sm:w-72">
          <CiSearch
            style={{
              position: "absolute",
              left: "8px",
              top: "50%",
              transform: "translateY(-50%)",
              color: theme.palette.text.main,
              pointerEvents: "none",
              fontSize: "16px",
            }}
          />
          <input
            type="text"
            placeholder="Search name or domain"
            aria-label="Search name or domain"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className={`border px-2 py-1 pl-8 text-sm rounded w-full focus:outline-none focus:ring-0 focus:border ${searchQuery ? "pr-8" : ""}`}
            style={{ borderColor: theme.palette.secondary.main, color: theme.palette.text.main }}
          />
          {searchQuery && (
            <button
              onClick={() => setSearchQuery("")}
              className="absolute right-2 top-1/2 -translate-y-1/2 text-accent-gray-medium hover:text-gray-700"
              aria-label="Clear search"
            >
              <XMarkIcon className="w-4 h-4" />
            </button>
          )}
        </div>
        <div className="flex gap-3 flex-wrap items-center">
        <FilterSelect
          label="Filter by status"
          value={statusFilter}
          onChange={(value) => {
            setStatusFilter(value);
            setCurrentPage(1);
          }}
        >
          <MenuItem value="">All statuses</MenuItem>
          <MenuItem value={ORG_STATUS.ACTIVE}>Active</MenuItem>
          <MenuItem value={ORG_STATUS.SUSPENDED}>Suspended</MenuItem>
          <MenuItem value={ORG_STATUS.DELETED}>Deleted</MenuItem>
        </FilterSelect>
        <FilterSelect
          label="Filter by plan"
          value={planFilter}
          onChange={(value) => {
            setPlanFilter(value);
            setCurrentPage(1);
          }}
        >
          <MenuItem value="">All plans</MenuItem>
          <MenuItem value={PLAN_FILTER.TRIAL}>Trial</MenuItem>
          {catalog
            .filter((plan) => !plan.contact_sales)
            .map((plan) => (
              <MenuItem key={plan.plan_id} value={plan.plan_id}>
                {plan.name}
              </MenuItem>
            ))}
          <MenuItem value={PLAN_FILTER.ENDED}>Plan ended</MenuItem>
          <MenuItem value={PLAN_FILTER.NONE}>No plan</MenuItem>
        </FilterSelect>
        <PrimaryButton onClick={() => setFormTarget(null)}>Create Organisation</PrimaryButton>
        </div> </div>
      <Notice onDismiss={() => setNotice("")}>{notice}</Notice>

      {isLoading ? (
        <Spinner label={clientMode ? "Checking plans for every organisation..." : "Loading organisations..."} />
      ) : (
        <>
          <div
            className="min-h-0 overflow-auto border rounded-md mx-6 max-md:mx-4 mt-4"
            style={{ borderColor: theme.palette.secondary.v4, backgroundColor: theme.palette.primary.main }}
          >
            {listError && (
              <div className="m-4">
                <ErrorBox>{listError}</ErrorBox>
              </div>
            )}
            {rows.length === 0 ? (
              listError ? null : (
                <div className="flex flex-col items-center justify-center h-80 text-accent-gray">
                  <p className="text-lg font-medium mb-1">No organisations found</p>
                  <p className="text-sm max-w-md text-center px-6">Try a different search or status.</p>
                </div>
              )
            ) : (
              <Table stickyHeader className="w-full min-w-max" sx={{ backgroundColor: theme.palette.primary.main }}>
                <TableHead>
                  <TableRow>
                    <TableCell sx={headerCellSx}>Organisation</TableCell>
                    <TableCell sx={headerCellSx}>Owner</TableCell>
                    <TableCell sx={headerCellSx}>
                      <TableSortLabel
                        active={Boolean(planSort)}
                        direction={planSort === "plan-desc" ? "desc" : "asc"}
                        onClick={() => {
                          // A to Z, then Z to A, then back to the server's order
                          setPlanSort((current) => (current === "" ? "plan-asc" : current === "plan-asc" ? "plan-desc" : ""));
                          setCurrentPage(1);
                        }}
                      >
                        Plan
                      </TableSortLabel>
                    </TableCell>
                    <TableCell sx={headerCellSx}>Status</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {rows.map((org) => (
                    <TableRow
                      key={org.id}
                      hover
                      tabIndex={0}
                      onClick={() => setPlanTarget(org)}
                      onKeyDown={(event) => {
                        if (event.target !== event.currentTarget) return;
                        if (event.key === "Enter" || event.key === " ") {
                          event.preventDefault();
                          setPlanTarget(org);
                        }
                      }}
                      aria-label={`View details for ${org.name || "organisation"}`}
                      sx={{ cursor: "pointer", "&:focus-visible": { outline: "2px solid #000000", outlineOffset: "-2px" } }}
                    >
                      <TableCell sx={cellSx}>
                        <div className="flex flex-col">
                          <span className="font-medium">{org.name || "—"}</span>
                          <span className="text-sm text-accent-gray-light">{org.domain || "No domain"}</span>
                        </div>
                      </TableCell>
                      <TableCell sx={cellSx}>
                        <div className="flex flex-col">
                          <span>{org.ownerName || "—"}</span>
                          <span className="text-sm text-accent-gray-light">{org.ownerEmail}</span>
                        </div>
                      </TableCell>
                      <TableCell sx={cellSx}>
                        <OrgPlanCell orgId={org.id} />
                      </TableCell>
                      <TableCell sx={cellSx}>
                        <StatusBadge status={org.status} />
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </div>
          {rows.length > 0 && (
            <div className="mt-auto">
              <Pager
                currentPage={currentPage}
                totalPages={totalPages}
                onPageChange={setCurrentPage}
                rowsPerPage={rowsPerPage}
                onRowsPerPageChange={(value) => {
                  setRowsPerPage(value);
                  setCurrentPage(1);
                }}
              />
            </div>
          )}
        </>
      )}

      <OrganisationFormModal
        isOpen={formTarget !== undefined}
        onClose={() => setFormTarget(undefined)}
        onSaved={(message) => {
          setNotice(message);
          load();
        }}
      />

      <OrgDetailsModal
        organisation={planTarget}
        onClose={() => setPlanTarget(null)}
        onSaved={setNotice}
        onOpen={handleEnter}
        opening={Boolean(planTarget) && enteringId === planTarget.id}
        onToggleSuspend={toggleSuspend}
        onEdited={async () => {
          const fresh = await load();
          setPlanTarget((current) => (current && fresh?.find((org) => org.id === current.id)) || current);
        }}
      />

      <ConfirmDialog
        isOpen={Boolean(confirm)}
        onClose={() => setConfirm(null)}
        onConfirm={runConfirm}
        title={confirm?.title}
        message={confirm?.message}
        confirmLabel={confirm?.confirmLabel}
        busyLabel={confirm?.busyLabel}
        busy={confirmBusy}
        error={confirmError}
        danger={confirm?.danger !== false}
        confirmText={confirm?.confirmText}
        typedValue={typedName}
        onTypedChange={setTypedName}
      />
    </div>
  );
};

export default SuperAdminPage;
